import { useMemo } from 'react';
import { AS8YNParam } from './params/AS8YNParam';
import { ASCoinChansParam } from './params/ASCoinChansParam';
import { ASColorParam } from './params/ASColorParam';
import { ASHolidayParam } from './params/ASHolidayParam';
import { ASNumericParam } from './params/ASNumericParam';
import { ASPrizeParam } from './params/ASPrizeParam';
import { ASScheduleParam } from './params/ASScheduleParam';
import { ASStringParam } from './params/ASStringParam';
import { ASUnknownParam } from './params/ASUnknownParam';
import { ASUserTagsParam } from './params/ASUserTagsParam';
import { MachineConfigParamDTO } from '@scm/common-legacy';

export interface ASParamEditorProps<ValueType = any> {
    param: MachineConfigParamDTO;
    currentValue: ValueType | null;
    realValue: ValueType | null;
    onCurrentValueChange: (newData: ValueType) => void;
    disabled: boolean;
}

export const ASParamEditor = (props: ASParamEditorProps<any>) => {
    const config = useMemo(
        () => getParamConfig(props.param),
        [props.param.type],
    );
    const validData = useMemo(
        () =>
            !config ||
            props.realValue == null ||
            config.valueValidator(props.realValue),
        [config, props.realValue],
    );

    if (config == null) return <ASUnknownParam />;
    if (!validData) return <span>Bad value format</span>;
    return config.component(props);
};

export const getParamConfig = (
    param: MachineConfigParamDTO,
): ASParamConfig<any> | null => {
    const paramType = param.type;

    if (paramType == 'vtByte') return NumericParam(param.max ?? 255);
    if (paramType == 'vtWORD') return NumericParam(param.max ?? 256 * 256 - 1);
    if (paramType == 'vtDWORD')
        return NumericParam(param.max ?? 256 * 256 * 256 * 256 - 1);

    if (paramType == 'vtString') return StringParam;
    if (paramType == 'vtPassword') return PasswordParam;
    if (paramType == 'vt8YN') return ListParam;
    if (paramType == 'vtColors') return ColorsParam;
    if (paramType == 'vtCoinChans') return CoinChansParam;
    if (paramType == 'vtHolidays') return HolidayParam;
    if (paramType == 'vtSchedule') return ScheduleParam;
    if (paramType == 'vtPrize') return PrizeParam;
    if (paramType == 'vtUserTags') return UserTagsParam;

    return null;
};

interface ASParamConfig<T = any> {
    icon: string;
    component: (view: ASParamEditorProps<T>) => JSX.Element;
    valueValidator: (val: T) => boolean;
}

// --- Params ---

const NumericParam = (maxValue: number): ASParamConfig<number> => ({
    icon: 'sort-numeric-down',
    component: (props) => <ASNumericParam {...props} maxValue={maxValue} />,
    valueValidator: (val) => typeof val == 'number',
});

// const NumericParam: ASParamConfig<number> = {
//     icon: "sort-numeric-down",
//     component: props => <ASNumericParam {...props}/>,
//     valueValidator: val => typeof val == "number"
// }

const StringParam: ASParamConfig<string> = {
    icon: 'text-width',
    component: (props) => <ASStringParam {...props} />,
    valueValidator: (val) => typeof val == 'string',
};

const PasswordParam: ASParamConfig<string> = {
    icon: 'key',
    component: (props) => <ASStringParam {...props} password={true} />,
    valueValidator: (val) => typeof val == 'string',
};

const ListParam: ASParamConfig<number> = {
    icon: 'list',
    component: (props) => <AS8YNParam {...props} />,
    valueValidator: (val) => typeof val == 'number',
};

const ColorsParam: ASParamConfig<number> = {
    icon: 'palette',
    component: (props) => <ASColorParam {...props} />,
    valueValidator: (val) => typeof val == 'number',
};

const CoinChansParam: ASParamConfig<{ pulses: number; value: number }[]> = {
    icon: 'coins',
    component: (props) => <ASCoinChansParam {...props} />,
    valueValidator: (val) =>
        Array.isArray(val) &&
        val.length == 6 &&
        val.every(
            (el) => typeof el.pulses == 'number' && typeof el.value == 'number',
        ),
};

const ScheduleParam: ASParamConfig<number[]> = {
    icon: 'calendar',
    component: (props) => <ASScheduleParam {...props} />,
    valueValidator: (val) =>
        Array.isArray(val) && val.every((v) => typeof v == 'number'),
};

const HolidayParam: ASParamConfig<
    { day: number; month: number; year: number }[]
> = {
    icon: 'calendar',
    component: (props) => <ASHolidayParam {...props} />,
    valueValidator: (val) =>
        Array.isArray(val) &&
        val.length == 64 &&
        val.every(
            (v) =>
                typeof v.day == 'number' &&
                typeof v.month == 'number' &&
                typeof v.year == 'number',
        ),
};

const PrizeParam: ASParamConfig<
    { boxAttached: number; qnty: number; filled: boolean }[]
> = {
    icon: 'gift',
    component: (props) => <ASPrizeParam {...props} />,
    valueValidator: (val) => Array.isArray(val) && val.length == 32,
};

const UserTagsParam: ASParamConfig<{ tag: number; options: number }[]> = {
    icon: 'user',
    component: (props) => <ASUserTagsParam {...props} />,
    valueValidator: (val) => Array.isArray(val),
};
